import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import i18n from "../../i18n";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LiveTv as LiveTvIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import PlacementBadge, { PlacementStrip } from "./PlacementBadge";
import {
  ProgramDetailBrandRowSkeleton,
  EXPLORER_PANEL_LIST_SX,
  EXPLORER_PANEL_CONTENT_SX,
} from "./ExplorerLoadingSkeletons";
import { EntityImage } from "../../components/EntityImage";
import { getS3Url, ensureDirectS3Url } from "../../utils/imageUtils";
import type { PlacementBucket } from "../../types/placement";
import { formatExplorerTableDateLine, formatExplorerTableTimeLine } from "./utils";
import apiService from "../../services/api";
import { LivestreamTimelineBlock } from "../../components/story/LivestreamTimelineBlock";

export type ExplorerSectionBrand = {
  _id: string;
  name: string;
  s3Key?: string;
  mentionCount?: number;
  totalMentions?: number;
  contentMentions?: number;
  physicalMentions?: number;
  creditsMentions?: number;
  bannerMentions?: number;
};

export type ExplorerBrandMention = {
  _id: string;
  startTime: number;
  createdAt?: string;
  placementType: PlacementBucket;
  content: {
    _id: string;
    title: string;
    youtubeId?: string;
    publishedAt?: string;
    analyzedAt?: string;
    duration?: number;
    programName?: string | null;
  } | null;
};

const LIVESTREAM_DURATION_TOLERANCE_SEC = 5 * 60;

type Props = {
  brands: ExplorerSectionBrand[];
  isLoading: boolean;
  isFetching: boolean;
  /** Scopes the per-brand mentions query cache (e.g. program or channel id + dates). */
  mentionsQueryKey: readonly unknown[];
  fetchMentions: (brandId: string) => Promise<{ mentions: ExplorerBrandMention[] }>;
  /** Show the program each episode belongs to (channel-level view). */
  showProgramName?: boolean;
};

const formatDetectedAt = (iso?: string): string => {
  if (!iso) return "—";
  const date = formatExplorerTableDateLine(iso);
  const time = formatExplorerTableTimeLine(iso);
  if (date === "—" || time === "—") return "—";
  return `${date} ${time}`;
};

const formatTimestamp = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const mentionTotal = (brand: ExplorerSectionBrand): number =>
  brand.totalMentions ?? brand.mentionCount ?? brand.contentMentions ?? 0;

const pinsToStart = (placementType: PlacementBucket): boolean =>
  placementType === "physical";

const mentionDisplayTime = (mention: {
  startTime: number;
  placementType: PlacementBucket;
}): number => (pinsToStart(mention.placementType) ? 0 : mention.startTime);

type MentionEpisode = {
  contentId: string;
  title: string;
  programName?: string | null;
  youtubeId?: string;
  publishedAt?: string;
  duration?: number;
  mentions: Array<{
    startTime: number;
    createdAt?: string;
    analyzedAt?: string;
    _id: string;
    placementType: PlacementBucket;
  }>;
};

const publishedAtTime = (value?: string): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const groupMentionsByEpisode = (mentions: ExplorerBrandMention[]): MentionEpisode[] => {
  const episodeMap = new Map<string, MentionEpisode>();
  mentions.forEach((mention) => {
    const contentId = mention.content?._id || "unknown";
    if (!episodeMap.has(contentId)) {
      episodeMap.set(contentId, {
        contentId,
        title: mention.content?.title || i18n.t("explorer:noEpisode"),
        programName: mention.content?.programName,
        youtubeId: mention.content?.youtubeId,
        publishedAt: mention.content?.publishedAt,
        duration: mention.content?.duration,
        mentions: [],
      });
    }
    episodeMap.get(contentId)!.mentions.push({
      startTime: mention.startTime,
      createdAt: mention.createdAt,
      analyzedAt: mention.content?.analyzedAt,
      _id: mention._id,
      placementType: mention.placementType,
    });
  });
  episodeMap.forEach((episode) => {
    episode.mentions.sort((a, b) => {
      const displayDiff = mentionDisplayTime(a) - mentionDisplayTime(b);
      if (displayDiff !== 0) return displayDiff;
      return a.startTime - b.startTime;
    });
  });
  return Array.from(episodeMap.values()).sort(
    (a, b) => publishedAtTime(b.publishedAt) - publishedAtTime(a.publishedAt),
  );
};

const EpisodeLivestreamMentionsPanel: React.FC<{
  youtubeId: string;
  contentDuration?: number;
  brandName?: string;
  brandS3Key?: string;
  mentions: MentionEpisode["mentions"];
}> = ({ youtubeId, contentDuration, brandName, brandS3Key, mentions }) => {
  const { t } = useTranslation("explorer");

  const {
    data: livestream,
    isLoading: livestreamLoading,
    isFetched: livestreamFetched,
  } = useQuery({
    queryKey: ["explorer-episode-livestream-by-video", youtubeId],
    queryFn: () => apiService.getLivestreamByVideoId(youtubeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const livestreamDurationSec =
    livestream?.youtubeDuration && livestream.youtubeDuration > 0
      ? livestream.youtubeDuration
      : livestream?.totalDuration && livestream.totalDuration > 0
        ? livestream.totalDuration
        : 0;

  const durationMismatch =
    !!livestream &&
    (contentDuration ?? 0) > 0 &&
    livestreamDurationSec > 0 &&
    Math.abs((contentDuration ?? 0) - livestreamDurationSec) >
      LIVESTREAM_DURATION_TOLERANCE_SEC;

  const canLoadTimeline = !!livestream?._id && !durationMismatch;

  const { data: snapshotsData, isLoading: snapshotsLoading } = useQuery({
    queryKey: ["explorer-episode-livestream-snapshots", livestream?._id],
    queryFn: () => apiService.getLivestreamSnapshots(livestream!._id),
    enabled: canLoadTimeline,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const timelineSnapshots = snapshotsData?.snapshots ?? [];
  const hasReleaseWindow = Boolean(
    livestream?.releaseTime &&
      livestream?.youtubeDuration != null &&
      livestream.youtubeDuration > 0,
  );
  const timelineTotalDuration = hasReleaseWindow
    ? livestream!.youtubeDuration!
    : (livestream?.totalDuration ??
      (livestream?.actualStartTime && livestream?.actualEndTime
        ? (new Date(livestream.actualEndTime).getTime() -
            new Date(livestream.actualStartTime).getTime()) /
          1000
        : 0));

  const mappedBrandMentions = useMemo(
    () =>
      mentions.map((m) => ({
        brandName: brandName || "Brand",
        mentionStartTime: mentionDisplayTime(m),
        mentionType: "paid" as const,
        s3Key: brandS3Key,
      })),
    [mentions, brandName, brandS3Key],
  );

  if (livestreamLoading || !livestreamFetched) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          {t("episodeLivestreamLoading")}
        </Typography>
      </Box>
    );
  }

  if (!livestream) {
    return null;
  }

  if (durationMismatch) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ py: 0.5 }}>
        {t("episodeLivestreamEdited")}
      </Typography>
    );
  }

  const livestreamById = livestream
    ? {
        ...livestream,
        chatMessageCount: livestream.finalChatCount ?? null,
        finalChatUniqueUsers: livestream.finalChatUniqueUsers ?? null,
      }
    : undefined;

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <LivestreamTimelineBlock
        videoId={youtubeId}
        livestreamById={livestreamById}
        timelineSnapshots={timelineSnapshots}
        timelineTotalDuration={timelineTotalDuration}
        brandMentions={mappedBrandMentions}
        includeChatStats
        isLoading={snapshotsLoading && timelineSnapshots.length === 0}
      />
    </Box>
  );
};

const EpisodeLivestreamToggle: React.FC<{
  youtubeId: string;
  open: boolean;
  onToggle: () => void;
}> = ({ youtubeId, open, onToggle }) => {
  const { t } = useTranslation("explorer");
  const { data: livestream, isFetched } = useQuery({
    queryKey: ["explorer-episode-livestream-by-video", youtubeId],
    queryFn: () => apiService.getLivestreamByVideoId(youtubeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  if (!isFetched || !livestream) {
    return null;
  }

  return (
    <Chip
      icon={<LiveTvIcon sx={{ fontSize: "14px !important" }} />}
      label={t("episodeLivestreamMentions")}
      size="small"
      variant="outlined"
      clickable
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        height: 20,
        fontSize: "0.7rem",
        flexShrink: 0,
        borderColor: open ? "primary.main" : "divider",
        color: open ? "primary.main" : "text.secondary",
        "& .MuiChip-icon": {
          color: open ? "primary.main" : "text.secondary",
          ml: 0.5,
        },
      }}
    />
  );
};

export const MentionEpisodeRows: React.FC<{
  mentions: ExplorerBrandMention[];
  brandName?: string;
  brandS3Key?: string;
}> = ({ mentions, brandName, brandS3Key }) => {
  const { t } = useTranslation("explorer");
  const episodes = groupMentionsByEpisode(mentions);
  const [expandedLivestreamContentId, setExpandedLivestreamContentId] =
    useState<string | null>(null);

  return (
    <Stack spacing={1}>
      {episodes.map((ep) => {
        const episodeDate = ep.publishedAt
          ? formatExplorerTableDateLine(ep.publishedAt)
          : null;
        const hasDate = Boolean(episodeDate && episodeDate !== "—");
        const livestreamOpen = expandedLivestreamContentId === ep.contentId;
        return (
          <Box key={ep.contentId}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {hasDate && (
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {episodeDate}
                  </Typography>
                )}
                {hasDate && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mx: 0.75 }}
                  >
                    ·
                  </Typography>
                )}
                {ep.title}
              </Typography>
              <Chip
                label={t("mentionCount", { count: ep.mentions.length })}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "primary.50",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              />
              {ep.youtubeId && (
                <EpisodeLivestreamToggle
                  youtubeId={ep.youtubeId}
                  open={livestreamOpen}
                  onToggle={() =>
                    setExpandedLivestreamContentId(
                      livestreamOpen ? null : ep.contentId,
                    )
                  }
                />
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {ep.mentions.map((mention) => {
                const displayTime = mentionDisplayTime(mention);
                const ytUrl = ep.youtubeId
                  ? `https://www.youtube.com/watch?v=${ep.youtubeId}&t=${Math.floor(displayTime)}s`
                  : null;
                return (
                  <Tooltip
                    key={mention._id}
                    title={t("placement.detectedAt", {
                      type: t(`placement.label.${mention.placementType}`),
                      time: formatDetectedAt(
                        mention.createdAt ?? mention.analyzedAt,
                      ),
                    })}
                    arrow
                    placement="top"
                  >
                    <Chip
                      component={ytUrl ? Link : "span"}
                      href={ytUrl || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <YouTubeIcon sx={{ fontSize: 14, color: "error.main" }} />
                          <PlacementBadge
                            bucket={mention.placementType}
                            dotOnly
                            hideTooltip
                          />
                        </span>
                      }
                      label={formatTimestamp(displayTime)}
                      size="small"
                      clickable={!!ytUrl}
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        textDecoration: "none",
                      }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
            {ep.youtubeId && (
              <Collapse in={livestreamOpen} timeout="auto" unmountOnExit>
                <EpisodeLivestreamMentionsPanel
                  youtubeId={ep.youtubeId}
                  contentDuration={ep.duration}
                  brandName={brandName}
                  brandS3Key={brandS3Key}
                  mentions={ep.mentions}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export const ExplorerBrandsSection: React.FC<Props> = ({
  brands,
  isLoading,
  isFetching,
  mentionsQueryKey,
  fetchMentions,
  showProgramName: _showProgramName = false,
}) => {
  const { t } = useTranslation("explorer");
  const [expandedBrandId, setExpandedBrandId] = useState<string | null>(null);

  const { data: brandMentionsData, isLoading: brandMentionsLoading } = useQuery({
    queryKey: [...mentionsQueryKey, expandedBrandId],
    queryFn: () => fetchMentions(expandedBrandId!),
    enabled: !!expandedBrandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const handleBrandClick = (brandId: string) => {
    setExpandedBrandId(expandedBrandId === brandId ? null : brandId);
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {isLoading ? (
          <Skeleton variant="text" width={220} height={36} />
        ) : (
          <Typography variant="h2">{t("sponsorsWithCount", { count: brands.length })}</Typography>
        )}
        {isFetching && !isLoading && <CircularProgress size={16} />}
      </Stack>

      {isLoading ? (
        <Paper sx={EXPLORER_PANEL_CONTENT_SX}>
          {Array.from({ length: 4 }, (_, i) => (
            <ProgramDetailBrandRowSkeleton key={`brand-skeleton-${i}`} />
          ))}
        </Paper>
      ) : brands.length === 0 ? (
        <Paper sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("noSponsorsDetected")}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List disablePadding sx={EXPLORER_PANEL_LIST_SX}>
            {brands.map((brand, index) => {
              const isExpanded = expandedBrandId === brand._id;
              const total = mentionTotal(brand);
              return (
                <React.Fragment key={brand._id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1,
                      px: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      bgcolor: isExpanded ? "action.selected" : "transparent",
                    }}
                    onClick={() => handleBrandClick(brand._id)}
                  >
                    <ListItemAvatar>
                      <EntityImage
                        name={brand.name}
                        src={
                          brand.s3Key
                            ? ensureDirectS3Url(getS3Url(brand.s3Key))
                            : undefined
                        }
                        size={32}
                        variant="plain"
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={brand.name}
                      secondary={
                        <PlacementStrip
                          contentMentions={brand.contentMentions}
                          physicalMentions={brand.physicalMentions}
                          bannerMentions={brand.bannerMentions}
                          creditsMentions={brand.creditsMentions}
                        />
                      }
                    />
                    <Chip
                      label={t("mentionCount", { count: total })}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ ...EXPLORER_PANEL_CONTENT_SX, pt: 1, pl: 7 }}>
                      {brandMentionsLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Cargando menciones...
                          </Typography>
                        </Box>
                      ) : !brandMentionsData?.mentions?.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          No se encontraron menciones
                        </Typography>
                      ) : (
                        <MentionEpisodeRows
                          mentions={brandMentionsData.mentions}
                          brandName={brand.name}
                          brandS3Key={brand.s3Key}
                        />
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}
    </>
  );
};
